import React, { useEffect, useState } from 'react';
import { Calendar, Loader2, CheckCircle, XCircle, Video, Clock, ExternalLink, Search, AlertCircle } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { checkBookingExpired } from '../../utils/calendarUtils';

/* Check if a booking's preferred date has passed */
const isBookingExpired = (booking) => {
  return checkBookingExpired(booking);
};

/* Shared promise timeout helper with cancellation support */
const withTimeout = (promise, ms = 15000, errorMsg = 'Operation timed out', abortController = null) => {
  let timerId;
  const timeoutPromise = new Promise((_, reject) => {
    timerId = setTimeout(() => {
      if (abortController) {
        try {
          abortController.abort();
        } catch (e) {
          console.error('Failed to abort request:', e);
        }
      }
      reject(new Error(errorMsg));
    }, ms);
  });

  return Promise.race([
    promise.then((res) => {
      clearTimeout(timerId);
      return res;
    }, (err) => {
      clearTimeout(timerId);
      throw err;
    }),
    timeoutPromise
  ]);
};

export default function AdminBookings() {
  const [bookings, setBookings] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');

  const [actionModal, setActionModal] = useState(null);
  const [meetingLink, setMeetingLink] = useState('');
  const [scheduledDate, setScheduledDate] = useState('');
  const [scheduledTime, setScheduledTime] = useState('');
  const [adminNote, setAdminNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [notification, setNotification] = useState({ type: '', text: '' });
  const [expiryProgress, setExpiryProgress] = useState(null);
  const [activeExpiryBookingId, setActiveExpiryBookingId] = useState(null);

  const showNotification = (type, text) => {
    setNotification({ type, text });
    setTimeout(() => {
      setNotification(prev => prev.text === text ? { type: '', text: '' } : prev);
    }, 5000);
  };

  const sendPush = (userId, title, body, url) => {
    const controller = new AbortController();
    return withTimeout(
      supabase.functions.invoke('send-push', {
        body: {
          user_id: userId,
          title,
          body,
          url
        },
        signal: controller.signal
      }),
      12000,
      'Push notification request timed out.',
      controller
    ).catch(e => {
      console.error('Error triggering push:', e);
      return { error: e };
    });
  };

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [bookingsRes, usersRes] = await Promise.all([
        supabase.from('bookings').select('*').order('created_at', { ascending: false }),
        supabase.from('profiles').select('id, first_name, last_name, subscription_plan'),
      ]);
      if (bookingsRes.error) throw bookingsRes.error;
      if (usersRes.error) throw usersRes.error;
      setUsers(usersRes.data || []);
      const enriched = (bookingsRes.data || []).map(b => {
        const u = (usersRes.data || []).find(x => x.id === b.user_id);
        return { ...b, user_name: u ? (`${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email || 'Client') : 'Client' };
      });
      setBookings(enriched);
    } catch (err) {
      console.error('Error fetching bookings:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async () => {
    if (!meetingLink.trim()) { showNotification('error', 'Please paste a meeting link.'); return; }
    setSaving(true);
    try {
      const confirmedDate = scheduledDate || actionModal.booking.preferred_date;
      const confirmedTime = scheduledTime || actionModal.booking.preferred_time;
      const { error } = await supabase.from('bookings').update({
        status: 'approved', meeting_link: meetingLink.trim(),
        scheduled_date: confirmedDate, scheduled_time: confirmedTime,
        admin_note: adminNote.trim() || null, updated_at: new Date().toISOString(),
      }).eq('id', actionModal.booking.id);
      if (error) throw error;

      await supabase.from('messages').insert({
        user_id: actionModal.booking.user_id,
        message: `✅ Booking Confirmed!\n\nYour strategy session has been scheduled for ${confirmedDate} at ${confirmedTime}.\n\n📹 Meeting Link: ${meetingLink.trim()}${adminNote.trim() ? `\n\nNote: ${adminNote.trim()}` : ''}\n\nSee you there!`,
        is_from_admin: true, admin_has_replied: true,
      });

      // Send real email
      try {
        await supabase.functions.invoke('send-email', {
          body: { 
            user_id: actionModal.booking.user_id, 
            subject: '✅ Strategy Session Confirmed!', 
            html: `
              <div style="font-family: sans-serif; max-width: 600px; margin: 0 auto; color: #334155;">
                <h2 style="color: #4F46E5;">Booking Confirmed!</h2>
                <p>Hello!</p>
                <p>Your strategy session with the Placement Point team has been confirmed.</p>
                <div style="background-color: #f8fafc; padding: 15px; border-radius: 8px; margin: 20px 0;">
                  <p><strong>Date:</strong> ${confirmedDate}</p>
                  <p><strong>Time:</strong> ${confirmedTime}</p>
                  <p><strong>Meeting Link:</strong> <a href="${meetingLink.trim()}">${meetingLink.trim()}</a></p>
                  ${adminNote.trim() ? `<p><strong>Note:</strong> ${adminNote.trim()}</p>` : ''}
                </div>
                <p>We look forward to speaking with you!</p>
                <br/>
                <p>Best regards,<br/>The Placement Point Team</p>
              </div>
            `
          }
        });
      } catch (emailErr) {
        console.error('Failed to send email:', emailErr);
      }

      // Send push notification to user
      const pushRes = await sendPush(
        actionModal.booking.user_id,
        'Booking Confirmed! ✅',
        `Your strategy session has been scheduled for ${confirmedDate} at ${confirmedTime}.`,
        '/dashboard/calendar'
      );

      closeModal(); await fetchData();

      if (pushRes && pushRes.error) {
        showNotification('warning', 'Booking approved, but push notification delivery failed.');
      } else {
        showNotification('success', 'Booking approved successfully.');
      }
    } catch (err) { console.error(err); showNotification('error', 'Failed to approve booking.'); }
    finally { setSaving(false); }
  };

  const handleReject = async () => {
    setSaving(true);
    try {
      const { error } = await supabase.from('bookings').update({
        status: 'rejected',
        admin_note: adminNote.trim() || 'Unfortunately, we cannot accommodate this time. Please try booking again.',
        updated_at: new Date().toISOString(),
      }).eq('id', actionModal.booking.id);
      if (error) throw error;

      await supabase.from('messages').insert({
        user_id: actionModal.booking.user_id,
        message: `❌ Booking Request Update\n\n${adminNote.trim() || 'Unfortunately, we cannot accommodate this time. Please try booking again with a different date/time.'}`,
        is_from_admin: true, admin_has_replied: true,
      });

      // Send push notification to user
      const pushRes = await sendPush(
        actionModal.booking.user_id,
        'Booking Request Update ❌',
        adminNote.trim() || 'Unfortunately, we cannot accommodate this time. Please try booking again.',
        '/dashboard/calendar'
      );

      closeModal(); await fetchData();

      if (pushRes && pushRes.error) {
        showNotification('warning', 'Booking rejected, but push notification delivery failed.');
      } else {
        showNotification('success', 'Booking rejected successfully.');
      }
    } catch (err) { console.error(err); showNotification('error', 'Failed to reject booking.'); }
    finally { setSaving(false); }
  };

  const handleComplete = async () => {
    setSaving(true);
    try {
      const { error } = await supabase.from('bookings').update({
        status: 'completed', admin_note: adminNote.trim() || actionModal.booking.admin_note,
        updated_at: new Date().toISOString(),
      }).eq('id', actionModal.booking.id);
      if (error) throw error;
      closeModal(); await fetchData();
    } catch (err) { console.error(err); showNotification('error', 'Failed to complete booking.'); }
    finally { setSaving(false); }
  };

  /* ── Expiry Notification Pipeline ── */
  const startExpiryFlow = (booking) => {
    if (activeExpiryBookingId) return;
    setActiveExpiryBookingId(booking.id);
    const alreadyExpired = booking.status === 'expired';
    const initial = {
      booking,
      status: 'running',
      steps: {
        dbUpdate: alreadyExpired ? 'success' : 'pending',
        chatMessage: 'pending',
        email: 'pending',
        push: 'pending',
      },
      errors: { dbUpdate: '', chatMessage: '', email: '', push: '' },
    };
    setExpiryProgress(initial);
    setTimeout(() => executeNextExpiryStep(initial), 100);
  };

  const executeNextExpiryStep = async (current) => {
    const booking = current.booking;

    // Step 1 — mark expired in DB
    if (current.steps.dbUpdate === 'pending') {
      setExpiryProgress(prev => ({ ...prev, steps: { ...prev.steps, dbUpdate: 'running' } }));
      try {
        const { error } = await withTimeout(
          supabase.from('bookings').update({
            status: 'expired',
            admin_note: 'This booking expired because the preferred date passed without a response. The client has been notified to rebook.',
            updated_at: new Date().toISOString(),
          }).eq('id', booking.id),
          12000,
          'Database update request timed out.'
        );
        if (error) throw error;
        const next = { ...current, steps: { ...current.steps, dbUpdate: 'success' } };
        setExpiryProgress(next);
        return executeNextExpiryStep(next);
      } catch (err) {
        setActiveExpiryBookingId(null);
        return setExpiryProgress(prev => ({
          ...prev, status: 'error',
          steps: { ...prev.steps, dbUpdate: 'failed' },
          errors: { ...prev.errors, dbUpdate: err.message || 'Failed to update database.' },
        }));
      }
    }

    // Step 2 — in-app chat message
    if (current.steps.chatMessage === 'pending') {
      setExpiryProgress(prev => ({ ...prev, steps: { ...prev.steps, chatMessage: 'running' } }));
      const controller = new AbortController();
      try {
        const { error } = await withTimeout(
          supabase.from('messages').insert({
            user_id: booking.user_id,
            message: `⏰ Booking Expired\n\nYour strategy session request for ${booking.preferred_date} at ${booking.preferred_time} has expired as the date has passed.\n\nPlease book a new session with an upcoming date and we'll get you scheduled as soon as possible!\n\nWe apologize for any inconvenience.`,
            is_from_admin: true, admin_has_replied: true,
          }).select().abortSignal(controller.signal),
          12000,
          'Chat message insertion request timed out.',
          controller
        );
        if (error) throw error;
        const next = { ...current, steps: { ...current.steps, chatMessage: 'success' } };
        setExpiryProgress(next);
        return executeNextExpiryStep(next);
      } catch (err) {
        setActiveExpiryBookingId(null);
        return setExpiryProgress(prev => ({
          ...prev, status: 'error',
          steps: { ...prev.steps, chatMessage: 'failed' },
          errors: { ...prev.errors, chatMessage: err.message || 'Failed to send chat message.' },
        }));
      }
    }

    // Step 3 — email notification
    if (current.steps.email === 'pending') {
      setExpiryProgress(prev => ({ ...prev, steps: { ...prev.steps, email: 'running' } }));
      const controller = new AbortController();
      try {
        const res = await withTimeout(
          supabase.functions.invoke('send-email', {
            body: {
              user_id: booking.user_id,
              subject: '⏰ Your Booking Has Expired — Please Rebook',
              html: `
                <div style="font-family: sans-serif; max-width: 600px; margin: 0 auto; color: #334155;">
                  <h2 style="color: #D97706;">⏰ Booking Expired</h2>
                  <p>Hello!</p>
                  <p>Your strategy session request for <strong>${booking.preferred_date}</strong> at <strong>${booking.preferred_time}</strong> has expired because the requested date has passed.</p>
                  <div style="background-color: #FFFBEB; border-left: 4px solid #F59E0B; padding: 15px; border-radius: 8px; margin: 20px 0;">
                    <p style="margin: 0; font-size: 14px;"><strong>What to do next:</strong> Please log in to your dashboard and book a new session with an upcoming date.</p>
                  </div>
                  <div style="text-align: center; margin: 32px 0;">
                    <a href="${window.location.origin}/dashboard/calendar" style="background-color: #4F46E5; color: #ffffff; padding: 14px 28px; text-decoration: none; border-radius: 12px; font-weight: bold; font-size: 14px; display: inline-block;">Book a New Session</a>
                  </div>
                  <p>We apologize for any inconvenience and look forward to speaking with you!</p>
                  <br/>
                  <p>Best regards,<br/>The Placement Point Team</p>
                </div>
              `
            },
            signal: controller.signal
          }),
          15000,
          'Email notification request timed out.',
          controller
        );
        if (res.error) {
          let errMsg = res.error.message || 'Failed to send email.';
          try {
            if (res.error.context) {
              const body = await res.error.context.json();
              if (body?.error) {
                errMsg = typeof body.error === 'string' ? body.error : (body.error.message || JSON.stringify(body.error));
              }
            }
          } catch (_) {}
          throw new Error(errMsg);
        }
        const next = { ...current, steps: { ...current.steps, email: 'success' } };
        setExpiryProgress(next);
        return executeNextExpiryStep(next);
      } catch (err) {
        setActiveExpiryBookingId(null);
        return setExpiryProgress(prev => ({
          ...prev, status: 'error',
          steps: { ...prev.steps, email: 'failed' },
          errors: { ...prev.errors, email: err.message || 'Failed to send email.' },
        }));
      }
    }

    // Step 4 — push notification
    if (current.steps.push === 'pending') {
      setExpiryProgress(prev => ({ ...prev, steps: { ...prev.steps, push: 'running' } }));
      const controller = new AbortController();
      try {
        const res = await withTimeout(
          sendPush(
            booking.user_id,
            'Booking Expired ⏰',
            'Your strategy session request has expired. Please rebook a new date/time.',
            '/dashboard/calendar'
          ),
          12000,
          'Push notification request timed out.',
          controller
        );
        if (res.error) throw new Error(typeof res.error === 'string' ? res.error : (res.error.message || JSON.stringify(res.error)));
        const next = { ...current, status: 'success', steps: { ...current.steps, push: 'success' } };
        setExpiryProgress(next);
        setActiveExpiryBookingId(null);
        await fetchData();
        return;
      } catch (err) {
        setActiveExpiryBookingId(null);
        return setExpiryProgress(prev => ({
          ...prev, status: 'error',
          steps: { ...prev.steps, push: 'failed' },
          errors: { ...prev.errors, push: err.message || 'Failed to send push notification.' },
        }));
      }
    }

    // All steps done
    setExpiryProgress(prev => ({ ...prev, status: 'success' }));
    setActiveExpiryBookingId(null);
    await fetchData();
  };

  const retryExpiryStep = (stepKey) => {
    if (!expiryProgress || activeExpiryBookingId) return;
    setActiveExpiryBookingId(expiryProgress.booking.id);
    const updated = {
      ...expiryProgress,
      status: 'running',
      steps: { ...expiryProgress.steps, [stepKey]: 'pending' },
      errors: { ...expiryProgress.errors, [stepKey]: '' },
    };
    setExpiryProgress(updated);
    executeNextExpiryStep(updated);
  };

  const openModal = (type, booking) => {
    setActionModal({ type, booking });
    setMeetingLink(booking.meeting_link || '');
    setScheduledDate(booking.scheduled_date || booking.preferred_date);
    setScheduledTime(booking.scheduled_time || booking.preferred_time);
    setAdminNote('');
  };

  const closeModal = () => {
    setActionModal(null); setMeetingLink(''); setScheduledDate(''); setScheduledTime(''); setAdminNote('');
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending': return 'bg-amber-100 text-amber-700';
      case 'approved': return 'bg-green-100 text-green-700';
      case 'rejected': return 'bg-red-100 text-red-700';
      case 'completed': return 'bg-slate-100 text-slate-600';
      case 'expired': return 'bg-orange-100 text-orange-700';
      default: return 'bg-slate-100 text-slate-600';
    }
  };

  const statuses = ['all', 'pending', 'approved', 'rejected', 'completed', 'expired'];
  const filteredBookings = bookings.filter(b => {
    const matchesSearch = b.user_name.toLowerCase().includes(searchQuery.toLowerCase()) || (b.topic || '').toLowerCase().includes(searchQuery.toLowerCase());
    // For filter: treat visually-expired pending bookings under 'expired' filter
    const effectiveStatus = isBookingExpired(b) ? 'expired' : b.status;
    return matchesSearch && (filterStatus === 'all' || effectiveStatus === filterStatus);
  });
  const pendingCount = bookings.filter(b => b.status === 'pending' && !isBookingExpired(b)).length;
  const expiredCount = bookings.filter(b => isBookingExpired(b) || b.status === 'expired').length;

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-indigo-950">
            Booking Requests
            {pendingCount > 0 && <span className="ml-3 inline-flex items-center justify-center w-7 h-7 bg-amber-500 text-white text-sm font-bold rounded-full">{pendingCount}</span>}
            {expiredCount > 0 && <span className="ml-2 inline-flex items-center justify-center w-7 h-7 bg-orange-500 text-white text-sm font-bold rounded-full" title="Expired bookings">{expiredCount}</span>}
          </h1>
          <p className="mt-1 text-slate-500">Manage client strategy session requests.</p>
        </div>
        <div className="flex items-center gap-3 w-full sm:w-auto">
          <div className="relative w-full sm:w-56">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input type="text" placeholder="Search clients..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-9 pr-4 py-2 border border-slate-200 rounded-xl text-sm focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 shadow-sm" />
          </div>
          <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}
            className="px-3 py-2 border border-slate-200 rounded-xl text-sm bg-white focus:outline-none focus:border-indigo-500 capitalize">
            {statuses.map(s => <option key={s} value={s}>{s === 'all' ? 'All Status' : s}</option>)}
          </select>
        </div>
      </div>

      {notification.text && (
        <div className={`p-4 rounded-xl border flex items-start gap-3 transition-all duration-300 animate-fadeIn ${
          notification.type === 'success'
            ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
            : notification.type === 'warning'
              ? 'bg-amber-50 border-amber-200 text-amber-800'
              : 'bg-rose-50 border-rose-200 text-rose-800'
        }`}>
          {notification.type === 'success' ? (
            <CheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
          ) : (
            <AlertCircle className={`w-5 h-5 flex-shrink-0 mt-0.5 ${notification.type === 'warning' ? 'text-amber-500' : 'text-rose-600'}`} />
          )}
          <div className="flex-1 text-sm font-medium pr-8 leading-relaxed">{notification.text}</div>
          <button onClick={() => setNotification({ type: '', text: '' })} className="text-slate-400 hover:text-slate-600 flex-shrink-0 focus:outline-none transition-colors">
            <span className="text-xl font-bold leading-none">&times;</span>
          </button>
        </div>
      )}

      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-indigo-600" /></div>
        ) : filteredBookings.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <Calendar className="w-12 h-12 text-slate-300 mb-3" />
            <p className="text-slate-500 font-medium">No booking requests found.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200 text-xs uppercase tracking-wider text-slate-500 font-bold">
                  <th className="p-4">Client</th><th className="p-4">Preferred Date/Time</th><th className="p-4">Topic</th>
                  <th className="p-4 text-center">Status</th><th className="p-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 text-sm">
                {filteredBookings.map((booking) => (
                  <tr key={booking.id} className="hover:bg-slate-50 transition-colors">
                    <td className="p-4 font-semibold text-indigo-900 whitespace-nowrap">{booking.user_name}</td>
                    <td className="p-4">
                      <div className="font-medium text-slate-900">{booking.preferred_date}</div>
                      <div className="text-xs text-slate-500">{booking.preferred_time}</div>
                    </td>
                    <td className="p-4 text-slate-600 max-w-[200px]"><span className="truncate block">{booking.topic || '—'}</span></td>
                    <td className="p-4 text-center whitespace-nowrap">
                      {isBookingExpired(booking) ? (
                        <span className={`px-2.5 py-1 text-xs font-bold rounded-full ${getStatusBadge('expired')}`}>Expired</span>
                      ) : (
                        <span className={`px-2.5 py-1 text-xs font-bold rounded-full capitalize ${getStatusBadge(booking.status)}`}>{booking.status}</span>
                      )}
                    </td>
                    <td className="p-4 text-right whitespace-nowrap">
                      <div className="flex items-center gap-2 justify-end">
                        {isBookingExpired(booking) ? (
                          <button
                            onClick={() => startExpiryFlow(booking)}
                            disabled={saving || activeExpiryBookingId !== null}
                            className="px-3 py-1.5 bg-orange-50 text-orange-700 rounded-lg text-xs font-bold hover:bg-orange-100 transition-colors disabled:opacity-50"
                          >
                            Notify to Rebook
                          </button>
                        ) : booking.status === 'pending' ? (
                          <>
                            <button onClick={() => openModal('approve', booking)} className="px-3 py-1.5 bg-green-50 text-green-700 rounded-lg text-xs font-bold hover:bg-green-100 transition-colors">Approve</button>
                            <button onClick={() => openModal('reject', booking)} className="px-3 py-1.5 bg-red-50 text-red-600 rounded-lg text-xs font-bold hover:bg-red-100 transition-colors">Reject</button>
                          </>
                        ) : null}
                        {booking.status === 'approved' && (
                          <button onClick={() => openModal('complete', booking)} className="px-3 py-1.5 bg-indigo-50 text-indigo-700 rounded-lg text-xs font-bold hover:bg-indigo-100 transition-colors">Mark Complete</button>
                        )}
                        {booking.meeting_link && (
                          <a href={booking.meeting_link} target="_blank" rel="noopener noreferrer" className="p-1.5 text-slate-400 hover:text-indigo-600 transition-colors">
                            <ExternalLink className="w-4 h-4" />
                          </a>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Action Modal */}
      {actionModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden">
            <div className={`p-5 border-b border-slate-100 flex justify-between items-center ${
              actionModal.type === 'approve' ? 'bg-green-50' : actionModal.type === 'reject' ? 'bg-red-50' : 'bg-indigo-50'
            }`}>
              <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2">
                {actionModal.type === 'approve' && <><CheckCircle className="w-5 h-5 text-green-600" /> Approve Booking</>}
                {actionModal.type === 'reject' && <><XCircle className="w-5 h-5 text-red-600" /> Reject Booking</>}
                {actionModal.type === 'complete' && <><CheckCircle className="w-5 h-5 text-indigo-600" /> Mark as Complete</>}
              </h3>
              <button onClick={closeModal} className="text-slate-400 hover:text-slate-600 text-xl">&times;</button>
            </div>

            <div className="p-6 space-y-4">
              <div className="bg-slate-50 p-3 rounded-lg text-sm">
                <p><strong>Client:</strong> {actionModal.booking.user_name}</p>
                <p><strong>Requested:</strong> {actionModal.booking.preferred_date} · {actionModal.booking.preferred_time}</p>
                {actionModal.booking.topic && <p><strong>Topic:</strong> {actionModal.booking.topic}</p>}
              </div>

              {actionModal.type === 'approve' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Confirmed Date *</label>
                    <input type="date" value={scheduledDate} onChange={(e) => setScheduledDate(e.target.value)}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-green-500" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Confirmed Time *</label>
                    <input type="text" value={scheduledTime} onChange={(e) => setScheduledTime(e.target.value)} placeholder="e.g. 2:00 PM - 3:00 PM"
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-green-500" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1"><Video className="w-4 h-4 inline mr-1" />Meeting Link *</label>
                    <input type="url" value={meetingLink} onChange={(e) => setMeetingLink(e.target.value)} placeholder="https://meet.google.com/xxx-xxxx-xxx"
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-green-500" />
                  </div>
                </>
              )}

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  {actionModal.type === 'reject' ? 'Reason for rejection' : 'Note to client'} (optional)
                </label>
                <textarea value={adminNote} onChange={(e) => setAdminNote(e.target.value)}
                  placeholder={actionModal.type === 'reject' ? 'e.g. That time is unavailable.' : 'e.g. Looking forward to our session!'}
                  rows={3} className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500 resize-none" />
              </div>

              <div className="flex gap-3 pt-2">
                <button onClick={closeModal} className="flex-1 px-4 py-2.5 border border-slate-200 text-slate-600 rounded-lg font-bold text-sm hover:bg-slate-50 transition-colors">Cancel</button>
                <button
                  onClick={actionModal.type === 'approve' ? handleApprove : actionModal.type === 'reject' ? handleReject : handleComplete}
                  disabled={saving}
                  className={`flex-1 px-4 py-2.5 rounded-lg font-bold text-sm transition-colors disabled:opacity-50 flex items-center justify-center gap-2 ${
                    actionModal.type === 'approve' ? 'bg-green-600 text-white hover:bg-green-700' :
                    actionModal.type === 'reject' ? 'bg-red-600 text-white hover:bg-red-700' :
                    'bg-indigo-600 text-white hover:bg-indigo-700'
                  }`}>
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                  {actionModal.type === 'approve' ? 'Approve & Send Link' : actionModal.type === 'reject' ? 'Reject Booking' : 'Mark Complete'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Expiry Progress Tracker Modal */}
      {expiryProgress && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden">
            <div className="p-5 border-b border-slate-100 flex justify-between items-center bg-orange-50">
              <h3 className="text-sm font-bold text-slate-900 flex items-center gap-2">
                <Clock className="w-5 h-5 text-orange-600" /> Expiry Notification Pipeline
              </h3>
              {expiryProgress.status !== 'running' && (
                <button onClick={() => { setExpiryProgress(null); fetchData(); }} className="text-slate-400 hover:text-slate-600 text-xl">&times;</button>
              )}
            </div>

            <div className="p-6 space-y-4">
              <div className="bg-slate-50 p-3 rounded-lg text-xs">
                <p><strong>Client:</strong> {expiryProgress.booking.user_name}</p>
                <p><strong>Slot:</strong> {expiryProgress.booking.preferred_date} &middot; {expiryProgress.booking.preferred_time}</p>
              </div>

              <div className="space-y-3">
                {[{ key: 'dbUpdate', label: '1. Update DB Status' },
                  { key: 'chatMessage', label: '2. Send In-App Message' },
                  { key: 'email', label: '3. Send Email Notification' },
                  { key: 'push', label: '4. Send Push Notification' }].map(({ key, label }) => (
                  <div key={key}>
                    <div className="flex items-center justify-between text-xs">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-slate-700">{label}</span>
                        {expiryProgress.steps[key] === 'running' && <Loader2 className="w-3.5 h-3.5 animate-spin text-indigo-600" />}
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`px-2 py-0.5 font-bold rounded text-[10px] capitalize ${
                          expiryProgress.steps[key] === 'success' ? 'bg-emerald-100 text-emerald-700' :
                          expiryProgress.steps[key] === 'failed' ? 'bg-rose-100 text-rose-700' :
                          expiryProgress.steps[key] === 'running' ? 'bg-indigo-100 text-indigo-700' : 'bg-slate-100 text-slate-500'
                        }`}>{expiryProgress.steps[key]}</span>
                        {expiryProgress.steps[key] === 'failed' && (
                          <button
                            onClick={() => retryExpiryStep(key)}
                            disabled={activeExpiryBookingId !== null}
                            className="text-indigo-600 hover:underline font-bold text-[11px] disabled:opacity-50"
                          >
                            Retry
                          </button>
                        )}
                      </div>
                    </div>
                    {expiryProgress.errors[key] && <p className="text-[10px] text-rose-600 pl-4 mt-0.5">{expiryProgress.errors[key]}</p>}
                  </div>
                ))}
              </div>

              {expiryProgress.status === 'success' && (
                <div className="bg-emerald-50 border border-emerald-200 text-emerald-800 p-3 rounded-xl text-xs font-semibold text-center">
                  ✅ All notifications sent successfully!
                </div>
              )}
              {expiryProgress.status === 'error' && (
                <div className="bg-rose-50 border border-rose-200 text-rose-800 p-3 rounded-xl text-xs font-semibold text-center">
                  ⚠️ A step failed. Click Retry next to the failed step to continue.
                </div>
              )}

              <div className="flex justify-end pt-2">
                <button
                  onClick={() => { setExpiryProgress(null); fetchData(); }}
                  disabled={expiryProgress.status === 'running'}
                  className="px-4 py-2 bg-slate-100 text-slate-700 text-xs font-bold rounded-xl hover:bg-slate-200 transition-colors disabled:opacity-50"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
